import React, { useEffect, useState } from "react";
import { useAuthCheck } from "../components/Auth";
import { useNavigate, useParams } from "react-router-dom";
import { api } from "../components/Api";
import Loading from "../components/Loading";

function EditUnityCard() {
    const { id } = useParams();
    const token = localStorage.getItem("token");
    const navigate = useNavigate();
    const { checkToken } = useAuthCheck();

    const [licenses, setLicenses] = useState([]);
    const [collections, setCollections] = useState([]);
    const [extensions, setExtensions] = useState([]);
    const [rarities, setRarities] = useState([]);
    const [languages, setLanguages] = useState([]);
    const [qualities, setQualities] = useState([]);
    const [links, setLinks] = useState([]);
    const [typeLinks, setTypeLinks] = useState([]);

    const [selectedLicense, setSelectedLicense] = useState("");
    const [filteredCollections, setFilteredCollections] = useState([]);
    const [filteredExtensions, setFilteredExtensions] = useState([]);

    const [loading, setLoading] = useState(true);
    const [form, setForm] = useState({
        name: "",
        reference: "",
        booster: false,
        buy: "",
        sell: "",
        commentary: "",
        license_card_id: "",
        collection_id: "",
        extension_id: "",
        rarity_card_id: "",
        language_id: "",
    });

    useEffect(() => { checkToken(); }, [checkToken]);

    // Charger toutes les données nécessaires et la carte
    useEffect(() => {
        const fetchData = async () => {
            try {
                const [
                    licensesRes,
                    collectionsRes,
                    extensionsRes,
                    raritiesRes,
                    languagesRes,
                    qualitiesRes,
                    typeLinksRes,
                    cardRes
                ] = await Promise.all([
                    api.get("/license-cards"),
                    api.get("/collections"),
                    api.get("/extensions"),
                    api.get("/rarity-cards"),
                    api.get("/languages"),
                    api.get("/quality-cards"),
                    api.get("/type-links"),
                    api.get(`/unity-cards/${id}`)
                ]);
                const data = cardRes.data;

                setLicenses(licensesRes.data);
                setCollections(collectionsRes.data);
                setExtensions(extensionsRes.data);
                setRarities(raritiesRes.data);
                setLanguages(languagesRes.data);
                setQualities(qualitiesRes.data);
                setTypeLinks(typeLinksRes.data);
                setLinks(data.links || []);
                setForm({
                    name: data.name || "",
                    reference: data.reference || "",
                    booster: data.booster ? true : false,
                    buy: data.buy || "",
                    sell: data.sell || "",
                    commentary: data.commentary || "",
                    license_card_id: data.license_card_id || "",
                    collection_id: data.collection_id || "",
                    extension_id: data.extension_id || "",
                    rarity_card_id: data.rarity_card_id || "",
                    language_id: data.language_id || "",
                });
                setSelectedLicense(data.license_card_id || "");
            } catch (err) {
                console.error("Erreur chargement données :", err);
                alert("Erreur lors du chargement des données.");
            } finally {
                setLoading(false);
            }
        };
        fetchData();
    }, [id]);

    // Filtrage des collections selon la licence
    useEffect(() => {
        if (selectedLicense) {
            const filtered = collections.filter(
                c => c.license_card_id === parseInt(selectedLicense)
            );
            setFilteredCollections(filtered);
            setFilteredExtensions([]);
            setForm(prev => ({
                ...prev,
                license_card_id: selectedLicense,
            }));
        } else {
            setFilteredCollections([]);
        }
    }, [selectedLicense, collections]);

    // Filtrage des extensions selon la collection
    useEffect(() => {
        if (form.collection_id) {
            const filtered = extensions.filter(
                ext => ext.collection_id === parseInt(form.collection_id)
            );
            setFilteredExtensions(filtered);
        } else {
            setFilteredExtensions([]);
        }
    }, [form.collection_id, extensions]);

    const handleChange = (e) => {
        const { name, value, type } = e.target;
        const newValue = type === "radio" ? value === "true" : value;

        if (name === "booster") {
            setForm(prev => ({
                ...prev,
                booster: newValue,
                rarity_card_id: newValue ? null : prev.rarity_card_id
            }));
        } else {
            setForm(prev => ({ ...prev, [name]: newValue }));
        }
    };

    const addLink = () => {
        setLinks(prev => [...prev, { id: null, type_link_id: "", link_url: "" }]);
    };

    // Fonction pour gérer la suppression d'un lien
    const removeLink = (index) => {
        setLinks(prev => prev.filter((_, i) => i !== index));
    };

    // Fonction pour gérer la modification d'un lien
    const handleLinkChange = (index, field, value) => {
        setLinks(prev => prev.map((link, i) => i === index ? { ...link, [field]: value } : link));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        const isBooster = form.booster;
        if (!isBooster && !form.rarity_card_id) {
            alert("Pour une carte unique, la rareté est obligatoire.");
            return;
        }

        const qualitiesPayload = !isBooster
            ? qualities.map(q => ({ id: q.id, quantity: 0 }))
            : [];

        const payload = {
            license_card_id: parseInt(form.license_card_id),
            collection_id: parseInt(form.collection_id),
            extension_id: form.extension_id ? parseInt(form.extension_id) : null,
            language_id: form.language_id ? parseInt(form.language_id) : null,
            rarity_card_id: isBooster ? null : parseInt(form.rarity_card_id),
            name: form.name,
            reference: form.reference,
            booster: isBooster ? 1 : 0,
            buy: form.buy ? parseFloat(form.buy) : null,
            sell: form.sell ? parseFloat(form.sell) : null,
            commentary: form.commentary,
            qualities: qualitiesPayload,
            links: links.map(l => ({
                id: l.id,
                type_link_id: l.type_link_id ? parseInt(l.type_link_id) : null,
                link_url: l.link_url
            }))
        };

        try {
            await api.put(`/unity-cards/${id}`, payload);
            alert("Carte mise à jour avec succès !");
            navigate(`/unity_card/${id}`);
        } catch (err) {
            console.error("Erreur :", err);
            if (err.response?.data?.errors) {
                alert("Erreur de validation : " + JSON.stringify(err.response.data.errors));
            } else {
                alert("Erreur lors de la mise à jour");
            }
        }
    };

    if (!token) navigate("/");
    if (loading) return <Loading message="Chargement des données..." />;

    return (
        <div className="container mx-auto p-4 space-y-6">
            <h1 className="text-2xl font-bold dark:text-white">Modifier la carte</h1>
            <form onSubmit={handleSubmit} className="space-y-6">
                {/* Licence / Collection / Extension / Langue */}
                <div className="p-4 border rounded bg-gray-100 dark:bg-gray-800 space-y-4">
                    <div>
                        <label>Licence :</label>
                        <select
                            name="license_card_id"
                            value={form.license_card_id}
                            onChange={e => setSelectedLicense(e.target.value)}
                            className="border p-2 w-full rounded focus:outline-none focus:ring-2 focus:ring-blue-400 dark:bg-gray-700 bg-white"
                            required
                        >
                            <option value="">-- Sélectionnez une licence --</option>
                            {licenses.map(l => (
                                <option key={l.id} value={l.id}>{l.license_card}</option>
                            ))}
                        </select>
                    </div>

                    {selectedLicense && (
                        <div>
                            <label>Collection :</label>
                            <select
                                name="collection_id"
                                value={form.collection_id}
                                onChange={handleChange}
                                className="border p-2 w-full rounded focus:outline-none focus:ring-2 focus:ring-blue-400 dark:bg-gray-700 bg-white"
                                required
                            >
                                <option value="">-- Sélectionnez une collection --</option>
                                {filteredCollections.map(c => (
                                    <option key={c.id} value={c.id}>{c.collection_name}</option>
                                ))}
                            </select>
                        </div>
                    )}

                    {form.collection_id && (
                        <>
                            <div>
                                <label>Extension :</label>
                                <select
                                    name="extension_id"
                                    value={form.extension_id}
                                    onChange={handleChange}
                                    className="border p-2 w-full rounded focus:outline-none focus:ring-2 focus:ring-blue-400 dark:bg-gray-700 bg-white"
                                >
                                    <option value="">-- Sélectionnez une extension --</option>
                                    {filteredExtensions.map(ext => (
                                        <option key={ext.id} value={ext.id}>{ext.extension_name}</option>
                                    ))}
                                </select>
                            </div>

                            <div>
                                <label>Langue :</label>
                                <select
                                    name="language_id"
                                    value={form.language_id}
                                    onChange={handleChange}
                                    className="border p-2 w-full rounded focus:outline-none focus:ring-2 focus:ring-blue-400 dark:bg-gray-700 bg-white"
                                    required
                                >
                                    <option value="">-- Sélectionnez une langue --</option>
                                    {languages.map(l => (
                                        <option key={l.id} value={l.id}>{l.language_name}</option>
                                    ))}
                                </select>
                            </div>
                        </>
                    )}

                    {form.language_id && (
                        <div className="flex items-center gap-4 mt-2">
                            <button
                                type="button"
                                onClick={() => handleChange({ target: { name: "booster", value: "false", type: "radio" } })}
                                className={`flex-1 p-3 rounded-lg border transition-all duration-200 ${!form.booster ? "bg-blue-500 text-white border-blue-600 shadow-md" : "bg-white text-gray-700 hover:bg-gray-200 dark:bg-gray-700 dark:text-gray-100"}`}
                            >
                                Carte unique
                            </button>
                            <button
                                type="button"
                                onClick={() => handleChange({ target: { name: "booster", value: "true", type: "radio" } })}
                                className={`flex-1 p-3 rounded-lg border transition-all duration-200 ${form.booster ? "bg-blue-500 text-white border-blue-600 shadow-md" : "bg-white text-gray-700 hover:bg-gray-200 dark:bg-gray-700 dark:text-gray-100"}`}
                            >
                                Booster
                            </button>
                        </div>
                    )}

                    {form.language_id && !form.booster && (
                        <div>
                            <label>Rareté :</label>
                            <select
                                name="rarity_card_id"
                                value={form.rarity_card_id}
                                onChange={handleChange}
                                className="border p-2 w-full rounded focus:outline-none focus:ring-2 focus:ring-blue-400 dark:bg-gray-700 bg-white"
                                required
                            >
                                <option value="">-- Sélectionnez une rareté --</option>
                                {rarities.map(r => (
                                    <option key={r.id} value={r.id}>{r.rarity_name}</option>
                                ))}
                            </select>
                        </div>
                    )}
                </div>

                {/* Infos carte */}
                {form.language_id && (
                    <>
                        <div className="p-4 border rounded bg-gray-100 dark:bg-gray-800 space-y-4">
                            <div className="flex items-center justify-between">
                                <label className="w-1/3 font-medium">Nom :</label>
                                <input
                                    type="text"
                                    name="name"
                                    value={form.name}
                                    onChange={handleChange}
                                    className="w-2/3 border p-2 rounded focus:outline-none focus:ring-2 focus:ring-blue-400 dark:bg-gray-700 bg-white"
                                    required
                                />
                            </div>
                            <div className="flex items-center justify-between">
                                <label className="w-1/3 font-medium">Référence :</label>
                                <input
                                    type="text"
                                    name="reference"
                                    value={form.reference}
                                    onChange={handleChange}
                                    className="w-2/3 border p-2 rounded focus:outline-none focus:ring-2 focus:ring-blue-400 dark:bg-gray-700 bg-white"
                                    required
                                />
                            </div>
                            <div className="flex items-center justify-between">
                                <label className="w-1/3 font-medium">Prix d'achat (€) :</label>
                                <input
                                    type="number"
                                    name="buy"
                                    step="0.01"
                                    value={form.buy}
                                    onChange={handleChange}
                                    className="w-2/3 border p-2 rounded focus:outline-none focus:ring-2 focus:ring-blue-400 dark:bg-gray-700 bg-white"
                                />
                            </div>
                            <div className="flex items-center justify-between">
                                <label className="w-1/3 font-medium">Côtation (€) :</label>
                                <input
                                    type="number"
                                    name="sell"
                                    step="0.01"
                                    value={form.sell}
                                    onChange={handleChange}
                                    className="w-2/3 border p-2 rounded focus:outline-none focus:ring-2 focus:ring-blue-400 dark:bg-gray-700 bg-white"
                                />
                            </div>
                            <div className="flex items-center justify-between">
                                <label className="w-1/3 font-medium">Prix de vente (€) :</label>
                                <input
                                    type="text"
                                    name="sell"
                                    value={`Near Mint : ${(Number(form.sell) || 0).toFixed(2)} €`}
                                    onChange={handleChange}
                                    className="w-1/6 border p-2 rounded focus:outline-none focus:ring-2 focus:ring-blue-400 dark:bg-gray-800 bg-white text-center"
                                    readOnly
                                />
                                <input
                                    type="text"
                                    name="sell"
                                    value={`Good : ${((Number(form.sell) || 0) * 0.8).toFixed(2)} €`}
                                    onChange={handleChange}
                                    className="w-1/6 border p-2 rounded focus:outline-none focus:ring-2 focus:ring-blue-400 dark:bg-gray-800 bg-white text-center"
                                    readOnly
                                />
                                <input
                                    type="text"
                                    name="sell"
                                    value={`Played : ${((Number(form.sell) || 0) * 0.6).toFixed(2)} €`}
                                    onChange={handleChange}
                                    className="w-1/6 border p-2 rounded focus:outline-none focus:ring-2 focus:ring-blue-400 dark:bg-gray-800 bg-white text-center"
                                    readOnly
                                />
                                <input
                                    type="text"
                                    name="sell"
                                    value={`Poor : ${((Number(form.sell) || 0) * 0.4).toFixed(2)} €`}
                                    onChange={handleChange}
                                    className="w-1/6 border p-2 rounded focus:outline-none focus:ring-2 focus:ring-blue-400 dark:bg-gray-800 bg-white text-center"
                                    readOnly
                                />
                            </div>
                        </div>

                        <div className="p-4 border rounded bg-gray-100 dark:bg-gray-800">
                            <label>Commentaire :</label>
                            <textarea
                                name="commentary"
                                value={form.commentary}
                                onChange={handleChange}
                                className="border p-1 w-full dark:bg-gray-700 bg-gray-050"
                                rows="4"
                            />
                        </div>
                        <div className="p-4 border rounded bg-gray-100 dark:bg-gray-800 space-y-2">
                            <h2 className="font-bold mb-2">Liens</h2>
                            {links.map((link, index) => (
                                <div key={index} className="flex items-center gap-2">
                                    <select
                                        value={link.type_link_id}
                                        onChange={e => handleLinkChange(index, "type_link_id", e.target.value)}
                                        className="border p-2 rounded w-1/3 dark:bg-gray-700 bg-white"
                                    >
                                        <option value="">-- Type de lien --</option>
                                        {typeLinks.map(t => (
                                            <option key={t.id} value={t.id}>{t.type_link_name}</option>
                                        ))}
                                    </select>
                                    <input
                                        type="url"
                                        value={link.link_url}
                                        onChange={e => handleLinkChange(index, "link_url", e.target.value)}
                                        placeholder="URL du lien"
                                        className="border p-2 rounded flex-1 dark:bg-gray-700 bg-white"
                                    />
                                    <button type="button" onClick={() => removeLink(index)} className="text-red-500 px-2">Supprimer</button>
                                </div>
                            ))}
                            <button type="button" onClick={addLink} className="bg-green-500 text-white px-4 py-2 rounded mt-2">
                                Ajouter un lien
                            </button>
                        </div>

                        <button type="submit" className="bg-blue-500 text-white px-4 py-2 rounded">
                            Mettre à jour la carte
                        </button>
                    </>
                )}
            </form>
        </div>
    );
}

export default EditUnityCard;
